import {Box, HBox} from '../../../components/core/view';
import React, {useCallback, useEffect} from 'react';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';

import BottomSheet from '../../../components/composites/bottomSheet';
import {CustomFlatList} from '../../../components/core/list';
import {Divider} from 'native-base';
import {H3Text, H5Text} from '../../../components/core/text';
import If from '../../../components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderItems} from '../../../batchDetails/types';
import {PButton} from '../../../components/core/Button';
import RecheckSheetItem from '../RecheckSheetItem';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {SpinnerComponent} from '../../../components/core/loader';
import font from '../../../appStyles/style/font';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';
import {useItemCheckBeforePacking} from '../../../batchDetails/hooks/OrderOperation';

interface RecheckBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  runId: number | undefined;
}

function RecheckBottomSheet(props: RecheckBottomSheetProps) {
  const {isOpen, onClose, onSubmit, runId} = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const mortiseFont = useFontTheme('mortise');
  const [itemCheckBeforePacking, checkState] = useItemCheckBeforePacking();
  const {body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const noRecheckData =
    !checkState.data?.recentOrderItem?.length &&
    !checkState.data?.substituteDroppedByShopper?.length &&
    !checkState.data?.substitutePickedByShopper?.length;

  const renderOrderItem = useCallback(
    ({item}: {item: OrderItems}) => {
      return <RecheckSheetItem runId={props?.runId} orderItem={item} />;
    },
    [props?.runId],
  );

  const groupTitle = useCallback(
    (title: string) => {
      return (
        <H5Text
          style={[
            styles.nameText,
            {
              color: theme?.text.blue[300],
              fontFamily: muliFont[700].normal,
            },
          ]}>
          {title}
        </H5Text>
      );
    },
    [muliFont, theme?.text.blue],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);

  useEffect(() => {
    if (isOpen && runId) itemCheckBeforePacking(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, runId]);

  return (
    <BottomSheet
      open={isOpen}
      onClose={onClose}
      bgColor={theme?.background.white[100]}>
      <If condition={checkState.loading}>
        <Box style={styles.loadingContainer}>
          <SpinnerComponent style={styles.loader} />
        </Box>
      </If>
      <If condition={!checkState.loading}>
        <Box style={[styles.mainContainer]}>
          <H3Text
            style={[
              styles.headerText,
              {
                color: theme?.text.blue[300],
                fontFamily: mortiseFont[700].normal,
              },
            ]}>
            {noRecheckData ? body?.recentChanges?.[0] : ''}
            {body?.recentChanges?.[1]}
          </H3Text>
          <CustomFlatList
            showsVerticalScrollIndicator={false}
            style={styles.scrollView}
            ListHeaderComponent={
              <Box>
                <If condition={checkState.data?.recentOrderItem?.length}>
                  {groupTitle(body?.recentlyAddedItems)}
                  <CustomFlatList
                    data={checkState.data?.recentOrderItem}
                    showsVerticalScrollIndicator={false}
                    renderItem={({item}) => renderOrderItem({item})}
                    ItemSeparatorComponent={Separator}
                    ListFooterComponent={Separator}
                  />
                </If>
                <If
                  condition={
                    checkState.data?.substituteDroppedByShopper?.length
                  }>
                  {groupTitle(body?.itemDropped)}
                  <CustomFlatList
                    showsVerticalScrollIndicator={false}
                    data={checkState.data?.substituteDroppedByShopper}
                    renderItem={({item}) =>
                      renderOrderItem({
                        item,
                      })
                    }
                    ItemSeparatorComponent={Separator}
                    ListFooterComponent={Separator}
                  />
                </If>
                <If
                  condition={
                    checkState.data?.substitutePickedByShopper?.length
                  }>
                  {groupTitle(body?.itemReplaced)}
                  <CustomFlatList
                    showsVerticalScrollIndicator={false}
                    data={checkState.data?.substitutePickedByShopper}
                    renderItem={({item}) =>
                      renderOrderItem({
                        item,
                      })
                    }
                    ItemSeparatorComponent={Separator}
                  />
                </If>
              </Box>
            }
            data={undefined}
            renderItem={undefined}
          />
          <HBox style={styles.buttonContainer}>
            <PButton
              title={button?.proceed}
              style={[styles.buttonStyleLeft]}
              bgColor={theme?.background.white[100]}
              titleStyle={{
                ...font.FontSize.fontH5,
                fontFamily: muliFont[700].normal,
              }}
              titleColor={theme?.text.blue[100]}
              onPress={onSubmit}
              borderColor={theme?.background.blue[100]}
              borderWidth={1}
            />
            <PButton
              title={button?.recheck}
              style={styles.buttonStyleRight}
              bgColor={theme?.background.blue[100]}
              titleStyle={{
                ...font.FontSize.fontH5,
                fontFamily: muliFont[700].normal,
              }}
              onPress={onClose}
            />
          </HBox>
        </Box>
      </If>
    </BottomSheet>
  );
}

export default React.memo(RecheckBottomSheet);
